/* Personalise Your Gift wizard.

   One custom element drives the whole modal: which tab is showing, what has
   been chosen on each, the running subtotal, and the walk to Add to Cart.

   Prices are integer cents throughout, matching Shopify's money fields — the
   subtotal must never accumulate float error before it is shown next to a
   real cart total.

   A selection is `{ price, variantId, ...tabSpecific }` keyed by tab slug.
   That shape is what Phase 5 turns into cart lines, so tabs record the fee
   variant they resolved rather than leaving it to be looked up again later. */

const GIFT_POSTCARD = { width: 350, height: 237, maxBytes: 10 * 1024 * 1024, quality: 0.9 };

/* Cart line-item property names. The underscore prefix keeps a property out of
   the customer-facing cart and checkout while still carrying it to the order,
   which is what lets the theme rebuild the group without showing plumbing. */
const GIFT_GROUP_PROP = '_gift_group';
const GIFT_ROLE_PROP = '_gift_role';

/* Tab slug -> the role its line plays in the group. */
const GIFT_ROLES = {
  sleeves: 'sleeve',
  postcard: 'postcard',
  engraving: 'engraving',
  ribbons: 'ribbon',
};

/* Paging for a horizontal scroll-snap track. Pages are measured from the laid
   out width rather than counted from the markup, so the same track shows two
   cards per page on desktop and scrolls one at a time on mobile without the
   server needing to know which. */
class GiftTrack {
  constructor(track, viewport) {
    this.track = track;
    this.viewport = viewport;
    const panel = track.closest('[data-gift-tab-panel]');
    this.arrows = panel.querySelector('[data-gift-track-arrows]');
    this.dots = panel.querySelector('[data-gift-track-dots]');
    this.caption = panel.querySelector('[data-gift-track-caption]');
    this.prev = panel.querySelector('[data-gift-track-prev]');
    this.next = panel.querySelector('[data-gift-track-next]');
    this.slides = Array.from(track.children);

    if (this.prev) this.prev.addEventListener('click', () => this.page(-1));
    if (this.next) this.next.addEventListener('click', () => this.page(1));
    track.addEventListener('scroll', () => this.scheduleSync(), { passive: true });

    /* The track is inside a hidden panel until its tab is opened, so it has no
       width to measure yet. Re-measure whenever it gains one. */
    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(() => this.sync()).observe(track);
    }
    this.sync();
  }

  get pageCount() {
    const width = this.track.clientWidth;
    if (!width) return 0;
    return Math.max(1, Math.round(this.track.scrollWidth / width));
  }

  get pageIndex() {
    const width = this.track.clientWidth;
    if (!width) return 0;
    return Math.round(this.track.scrollLeft / width);
  }

  scheduleSync() {
    if (this.pending) return;
    this.pending = true;
    requestAnimationFrame(() => {
      this.pending = false;
      this.sync();
    });
  }

  page(delta) {
    this.track.scrollBy({ left: this.track.clientWidth * delta, behavior: 'smooth' });
  }

  sync() {
    const pages = this.pageCount;
    const index = this.pageIndex;
    const paged = pages > 1;

    if (this.arrows) this.arrows.hidden = !paged;
    if (this.dots) this.dots.hidden = !paged;
    if (this.prev) this.prev.disabled = index === 0;
    if (this.next) this.next.disabled = index >= pages - 1;

    if (this.dots && this.dots.childElementCount !== pages) this.buildDots(pages);
    if (this.dots) {
      Array.from(this.dots.children).forEach((dot, i) => {
        dot.setAttribute('aria-current', String(i === index));
      });
    }

    if (this.caption) {
      const slide = this.slides[Math.min(index, this.slides.length - 1)];
      this.caption.textContent = slide ? slide.dataset.giftSlideLabel || '' : '';
    }
  }

  buildDots(pages) {
    this.dots.textContent = '';
    for (let i = 0; i < pages; i += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'gift-pager__dot';
      dot.setAttribute('aria-label', `Go to ${i + 1}`);
      dot.addEventListener('click', () => {
        this.track.scrollTo({ left: this.track.clientWidth * i, behavior: 'smooth' });
      });
      this.dots.appendChild(dot);
    }
  }
}

class GiftPersonalise extends HTMLElement {
  constructor() {
    super();
    /* Seeded here rather than in connectedCallback so a variant change
       arriving mid-upgrade can still read a subtotal. */
    this.selections = new Map();
  }

  connectedCallback() {
    /* The shared modal relocates itself to <body> on first connect, which
       disconnects and reconnects this element. Wiring is one-time, but the
       variant subscription is torn down on disconnect and has to come back. */
    this.followVariantPrice();
    if (this.initialised) return;
    this.initialised = true;

    this.tabSlugs = (this.dataset.tabs || '').split(',').filter(Boolean);
    if (!this.tabSlugs.length) return;

    this.tabButtons = Array.from(this.querySelectorAll('[data-gift-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-gift-tab-panel]'));
    this.prevButton = this.querySelector('[data-gift-prev]');
    this.nextButton = this.querySelector('[data-gift-next]');
    this.nextLabel = this.querySelector('[data-gift-next-label]');
    this.nextSpinner = this.nextButton ? this.nextButton.querySelector('.loading__spinner') : null;
    this.subtotalOutput = this.querySelector('[data-gift-subtotal]');
    this.submitError = this.querySelector('[data-gift-submit-error]');
    this.grandTotalOutput = this.querySelector('[data-gift-grand-total]');
    /* Read from an attribute, not from textContent: the element ships with a
       server-rendered total so the footer is correct before this script runs,
       and reusing that as the template would bake the first price in. */
    this.grandTotalTemplate = this.grandTotalOutput ? this.grandTotalOutput.dataset.template || '' : '';

    this.tabButtons.forEach((button) => {
      button.addEventListener('click', () => this.activate(this.tabSlugs.indexOf(button.dataset.giftTab)));
    });
    if (this.prevButton) this.prevButton.addEventListener('click', () => this.step(-1));
    if (this.nextButton) this.nextButton.addEventListener('click', () => this.step(1));

    this.hideMrpInput = this.querySelector('[data-gift-hide-mrp]');
    if (this.hideMrpInput) {
      this.hideMrpInput.addEventListener('change', () => this.syncHideMrp());
    }

    this.initTracks();
    this.initOptions();
    this.initOptins();
    this.initEngraving();
    this.initPostcard();

    this.activate(0);
    this.renderTotals();
    this.syncHideMrp();
    this.bindModalLifecycle();
    this.interceptFormSubmit();
  }

  /* ---------- panel wiring ---------- */

  initTracks() {
    this.tracks = Array.from(this.querySelectorAll('[data-gift-track]')).map(
      (track) => new GiftTrack(track, track.closest('.gift-viewport'))
    );
  }

  /* Sleeves and Gift Wrap: radio-backed cards, so the browser enforces
     single-select and this only has to report the result. */
  initOptions() {
    this.querySelectorAll('[data-gift-option-input]').forEach((input) => {
      input.addEventListener('change', () => {
        if (!input.checked) return;
        const tab = input.closest('[data-gift-tab-panel]').dataset.giftTabPanel;
        this.setSelection(tab, {
          price: parseInt(input.dataset.price, 10) || 0,
          variantId: input.value,
          label: input.dataset.label || '',
          sku: input.dataset.sku || '',
        });
      });
    });
  }

  initOptins() {
    this.querySelectorAll('[data-gift-optin-input]').forEach((input) => {
      input.addEventListener('change', () => {
        this.syncOptin(input);
        /* Say so the moment they opt in with nothing typed, rather than
           letting them reach the last tab before finding out. */
        if (input.dataset.tab === 'engraving') this.validateEngraving();
      });
    });
  }

  syncOptin(input) {
    const tab = input.dataset.tab;
    if (!input.checked) {
      this.clearSelection(tab);
      return;
    }
    const detail = {
      price: parseInt(input.dataset.price, 10) || 0,
      variantId: input.value,
    };
    if (tab === 'engraving') {
      detail.text = this.engravingInput ? this.engravingInput.value : '';
      detail.font = this.activeFont ? this.activeFont.dataset.fontName : '';
    }
    if (tab === 'postcard') {
      detail.message = this.postcardInput ? this.postcardInput.value : '';
      detail.image = this.postcardBlob || null;
      detail.generic = !this.postcardBlob;
    }
    this.setSelection(tab, detail);
  }

  /* Re-reports a tab that is already opted in, so edits to its text, font or
     photo reach the selection without the customer re-ticking the box. */
  refreshOptin(tab) {
    const input = this.querySelector(`[data-gift-optin-input][data-tab="${tab}"]`);
    if (input && input.checked) this.syncOptin(input);
  }

  /* ---------- engraving ---------- */

  initEngraving() {
    this.fontButtons = Array.from(this.querySelectorAll('[data-gift-font]'));
    this.engravingInput = this.querySelector('[data-gift-engraving-input]');
    this.engravingPreview = this.querySelector('[data-gift-engraving-preview]');
    this.engravingLimitOutput = this.querySelector('[data-gift-engraving-limit]');
    this.engravingError = this.querySelector('[data-gift-engraving-error]');
    this.fontNameOutput = this.querySelector('[data-gift-font-name]');

    this.fontButtons.forEach((button) => {
      button.addEventListener('click', () => this.selectFont(button));
    });
    if (this.fontButtons.length) this.selectFont(this.fontButtons[0]);

    if (this.engravingInput) {
      this.engravingInput.addEventListener('input', () => {
        if (this.engravingPreview) this.engravingPreview.textContent = this.engravingInput.value;
        this.refreshOptin('engraving');
        /* Clear the complaint as soon as they start typing, but do not raise
           it here — nagging mid-word is not the same as refusing at commit. */
        if (this.engravingText) this.showEngravingError(false);
      });
    }
  }

  get engravingText() {
    return this.engravingInput ? this.engravingInput.value.trim() : '';
  }

  get engravingOptedIn() {
    const optin = this.querySelector('[data-gift-optin-input][data-tab="engraving"]');
    return !!optin && optin.checked;
  }

  showEngravingError(show) {
    if (this.engravingError) this.engravingError.hidden = !show;
  }

  /* An engraving line with no text bills the customer for nothing and gives
     the engraver nothing to cut, so the whole add is refused rather than the
     empty engraving being quietly dropped from it. */
  validateEngraving() {
    const invalid = this.engravingOptedIn && !this.engravingText;
    this.showEngravingError(invalid);
    return !invalid;
  }

  selectFont(button) {
    this.activeFont = button;
    this.fontButtons.forEach((other) => other.setAttribute('aria-pressed', String(other === button)));

    const name = button.dataset.fontName || '';
    if (this.fontNameOutput) this.fontNameOutput.textContent = name;
    /* `inherit` is not a valid entry in a font-family LIST — including it
       invalidates the whole declaration and the preview silently keeps the
       body face. The fallback has to be a real family. */
    if (this.engravingPreview) {
      this.engravingPreview.style.fontFamily = `'${name}', var(--font-jakarta-regular)`;
    }

    /* Each font engraves a different number of characters, so the cap moves
       with the choice — and anything already over it is cut rather than left
       to fail at the engraver. */
    const limit = parseInt(button.dataset.fontLimit, 10) || 0;
    if (limit && this.engravingInput) {
      this.engravingInput.maxLength = limit;
      if (this.engravingInput.value.length > limit) {
        this.engravingInput.value = this.engravingInput.value.slice(0, limit);
        if (this.engravingPreview) this.engravingPreview.textContent = this.engravingInput.value;
      }
    }
    if (this.engravingLimitOutput) this.engravingLimitOutput.textContent = String(limit);
    this.refreshOptin('engraving');
  }

  /* ---------- post card ---------- */

  initPostcard() {
    this.postcardInput = this.querySelector('[data-gift-postcard-input]');
    this.postcardMessage = this.querySelector('[data-gift-postcard-message]');
    this.postcardPreview = this.querySelector('[data-gift-postcard-preview]');
    this.uploadInput = this.querySelector('[data-gift-upload-input]');
    this.uploadThumb = this.querySelector('[data-gift-upload-thumb]');
    this.uploadName = this.querySelector('[data-gift-upload-name]');
    this.uploadHint = this.querySelector('[data-gift-upload-hint]');
    this.uploadReplace = this.querySelector('[data-gift-upload-replace]');
    this.uploadError = this.querySelector('[data-gift-upload-error]');
    /* The thumbnail ships showing the generic card, so "use generic" restores
       that image rather than blanking the box. */
    this.genericThumbSrc = this.uploadThumb ? this.uploadThumb.getAttribute('src') : '';

    if (this.postcardInput) {
      this.postcardInput.addEventListener('input', () => {
        if (this.postcardMessage) this.postcardMessage.textContent = this.postcardInput.value;
        this.refreshOptin('postcard');
      });
    }

    const openPicker = () => this.uploadInput && this.uploadInput.click();
    const openButton = this.querySelector('[data-gift-upload-open]');
    if (openButton) openButton.addEventListener('click', openPicker);
    if (this.uploadReplace) this.uploadReplace.addEventListener('click', openPicker);

    const genericButton = this.querySelector('[data-gift-upload-generic]');
    if (genericButton) genericButton.addEventListener('click', () => this.useGenericCard());

    if (this.uploadInput) {
      this.uploadInput.addEventListener('change', () => {
        const file = this.uploadInput.files && this.uploadInput.files[0];
        if (file) this.acceptPhoto(file);
      });
    }
  }

  showUploadError(message) {
    if (!this.uploadError) return;
    this.uploadError.textContent = message;
    this.uploadError.hidden = !message;
  }

  /* The crop is the only gate on the photo — nothing server-side inspects it —
     so every rejection has to be visible here rather than swallowed. */
  acceptPhoto(file) {
    this.showUploadError('');

    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
      this.showUploadError('That file is not a JPG, PNG or WebP image.');
      return;
    }
    if (file.size > GIFT_POSTCARD.maxBytes) {
      this.showUploadError('That image is over 10mb. Please choose a smaller one.');
      return;
    }

    this.cropToCard(file)
      .then((blob) => {
        if (this.postcardBlobUrl) URL.revokeObjectURL(this.postcardBlobUrl);
        this.postcardBlob = blob;
        this.postcardBlobUrl = URL.createObjectURL(blob);

        if (this.postcardPreview) {
          this.postcardPreview.src = this.postcardBlobUrl;
          this.postcardPreview.hidden = false;
        }
        if (this.uploadThumb) this.uploadThumb.src = this.postcardBlobUrl;
        if (this.uploadName) this.uploadName.textContent = file.name;
        if (this.uploadHint) this.uploadHint.textContent = 'Uploaded · shows on the card front';
        if (this.uploadReplace) this.uploadReplace.hidden = false;
        this.refreshOptin('postcard');
      })
      .catch(() => this.showUploadError('That image could not be read. Please try another.'));
  }

  useGenericCard() {
    if (this.postcardBlobUrl) URL.revokeObjectURL(this.postcardBlobUrl);
    this.postcardBlob = null;
    this.postcardBlobUrl = null;

    if (this.postcardPreview) {
      this.postcardPreview.hidden = true;
      this.postcardPreview.removeAttribute('src');
    }
    if (this.uploadThumb) this.uploadThumb.src = this.genericThumbSrc;
    if (this.uploadName) this.uploadName.textContent = 'Generic card';
    if (this.uploadHint) this.uploadHint.textContent = 'Uses the generic card front';
    if (this.uploadReplace) this.uploadReplace.hidden = true;
    if (this.uploadInput) this.uploadInput.value = '';
    this.showUploadError('');
    this.refreshOptin('postcard');
  }

  /* Centre-crops to the printed card's exact pixel size. Done here because
     the cart accepts whatever the browser sends — there is no second chance
     to fix the aspect ratio further down. */
  cropToCard(file) {
    const { width, height, quality } = GIFT_POSTCARD;
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const image = new Image();
      image.onload = () => {
        URL.revokeObjectURL(url);
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const scale = Math.max(width / image.width, height / image.height);
        const drawWidth = image.width * scale;
        const drawHeight = image.height * scale;
        canvas
          .getContext('2d')
          .drawImage(image, (width - drawWidth) / 2, (height - drawHeight) / 2, drawWidth, drawHeight);

        canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('crop failed'))), 'image/jpeg', quality);
      };
      image.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('decode failed'));
      };
      image.src = url;
    });
  }

  /* ---------- selections + totals ---------- */

  setSelection(tab, detail) {
    this.selections.set(tab, detail);
    this.renderTotals();
    this.dispatchEvent(
      new CustomEvent('gift:selection-change', { bubbles: true, detail: { tab, ...detail } })
    );
  }

  clearSelection(tab) {
    this.selections.delete(tab);
    this.renderTotals();
    this.dispatchEvent(
      new CustomEvent('gift:selection-change', { bubbles: true, detail: { tab, price: 0 } })
    );
  }

  get productPrice() {
    return parseInt(this.dataset.productPrice, 10) || 0;
  }

  get subtotal() {
    let total = 0;
    this.selections.forEach((detail) => {
      total += detail.price || 0;
    });
    return total;
  }

  renderTotals() {
    const subtotal = this.subtotal;
    if (this.subtotalOutput) this.subtotalOutput.textContent = this.money(subtotal);
    if (this.grandTotalOutput) {
      this.grandTotalOutput.textContent = this.grandTotalTemplate.replace(
        '[total]',
        this.money(this.productPrice + subtotal)
      );
    }
  }

  money(cents) {
    if (typeof formatMoney !== 'function') return String(cents / 100);
    return formatMoney(cents, window.moneyFormat).replace(/\.00$/, '');
  }

  /* ---------- navigation ---------- */

  activate(index) {
    if (index < 0 || index >= this.tabSlugs.length) return;
    this.activeIndex = index;
    const slug = this.tabSlugs[index];

    this.tabButtons.forEach((button) => {
      button.setAttribute('aria-selected', String(button.dataset.giftTab === slug));
    });
    this.panels.forEach((panel) => {
      panel.hidden = panel.dataset.giftTabPanel !== slug;
    });

    if (this.prevButton) this.prevButton.disabled = index === 0;

    if (this.nextButton && this.nextLabel) {
      const isLast = index === this.tabSlugs.length - 1;
      const { labelNext, labelSubmit } = this.nextButton.dataset;
      /* Written to the label span, not the button — the button also holds the
         spinner, which textContent would throw away. */
      this.nextLabel.textContent = isLast ? labelSubmit : labelNext;
    }

    const activeButton = this.tabButtons[index];
    if (activeButton && typeof activeButton.scrollIntoView === 'function') {
      activeButton.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }

    /* Tracks in a hidden panel have no width, so their page count is only
       measurable once the panel is shown. */
    if (this.tracks) this.tracks.forEach((track) => track.sync());

    this.dispatchEvent(new CustomEvent('gift:tab-change', { bubbles: true, detail: { tab: slug, index } }));
  }

  step(delta) {
    const next = this.activeIndex + delta;
    /* Past the last tab there is nowhere to walk to — that is where the
       wizard commits instead of advancing. */
    if (next >= this.tabSlugs.length) {
      this.submit();
      return;
    }
    this.activate(next);
  }

  submit() {
    /* Checked before the empty-selection shortcut below: an engraving opted
       into but left blank must stop the add, not fall through to a plain one. */
    if (!this.validateEngraving()) {
      this.activate(this.tabSlugs.indexOf('engraving'));
      if (this.engravingInput) this.engravingInput.focus();
      return;
    }

    if (!this.productForm) {
      /* Closing here would look like a successful add. Stay open and say so. */
      this.showSubmitError('Could not find the product form. Please reload the page.');
      return;
    }
    /* Every add goes through addToCart, including one with nothing chosen.
       Handing an empty selection back to the theme's own submit would close
       the modal first and leave the customer on a bare product page for the
       length of the request; here the modal holds its spinner until the cart
       answers. addToCart drops the group properties when there is no group,
       so a plain add still produces a plain line. */
    this.addToCart();
  }

  /* Every line added by one click shares a token, and each says what it is.
     That token is what lets the cart rebuild the group later: without it the
     only link between a product and its engraving is the engraving text, and
     two products engraved with the same word produce identical properties,
     which Shopify merges into a single line belonging to both. */
  giftToken() {
    if (window.crypto && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    return `g${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  }

  /* The bundle add-on picker runs its own submit interception, which builds
     its item list by hand and would drop these lines. Whichever handler wins
     has to carry both, so the grouped path folds the add-ons in here. */
  bundleAddonItems() {
    const container = document.querySelector(`[data-pdp-addon][data-section-id="${this.dataset.sectionId}"]`);
    if (!container) return [];
    const selected = container.querySelectorAll(
      '[data-addon-card][data-addon-selected="true"]:not([data-addon-unavailable="true"])'
    );
    return Array.from(selected).map((card) => ({
      id: parseInt(card.dataset.addonVariantId, 10),
      quantity: 1,
      properties: {
        __source: 'pdp-mostly-paired-with',
        'Added From URL': window.location.origin + window.location.pathname,
      },
    }));
  }

  buildGiftItems(token) {
    const form = this.productForm;
    const idInput = form.querySelector('input[name="id"]');
    const qtyInput = form.querySelector('input[name="quantity"]');

    /* Only stamp the group when there is a group. A lone product carrying a
       token and a `parent` role would tell fulfilment it belongs to a gift
       that has no other parts. */
    const grouped = this.selections.size > 0;
    const parentProperties = grouped ? { [GIFT_GROUP_PROP]: token, [GIFT_ROLE_PROP]: 'parent' } : {};
    const engraving = this.selections.get('engraving');
    /* The parent carries the engraving spec because the parent is the object
       being engraved — the cart and the packing slip both read it from here.
       Text and font are written as a pair, and the fee line below repeats the
       same pair: an engraving described by text alone cannot be produced, so
       the two must never end up on different lines. */
    if (engraving && engraving.text) {
      parentProperties['Engraving Text'] = engraving.text;
      if (engraving.font) parentProperties['Engraving Font'] = engraving.font;
    }
    /* Fulfilment cross-link: the parent carries the chosen gift-wrap's SKU so the pack
       slip shows which wrap goes with this product (the wrap line carries the reverse —
       this product's handle). Only when a gift wrap was actually chosen. */
    const ribbonSel = this.selections.get('ribbons');
    if (ribbonSel && ribbonSel.sku) parentProperties['_gift_wrap_sku'] = ribbonSel.sku;
    if (this.hideMrpInput && this.hideMrpInput.checked) parentProperties['Hide MRP'] = 'Yes';

    const parentItem = {
      id: parseInt(idInput.value, 10),
      quantity: parseInt(qtyInput ? qtyInput.value : '1', 10) || 1,
    };
    /* Omitted rather than sent empty, so an unpersonalised add is byte-for-byte
       the ordinary one. */
    if (Object.keys(parentProperties).length) parentItem.properties = parentProperties;

    const items = [parentItem, ...this.bundleAddonItems()];

    let postcardUpload = null;
    this.selections.forEach((detail, tab) => {
      if (!detail.variantId) return;
      const properties = { [GIFT_GROUP_PROP]: token, [GIFT_ROLE_PROP]: GIFT_ROLES[tab] || tab };

      /* Fulfilment cross-link: the gift-wrap line carries the parent product's handle so
         the pack slip can trace the wrap back to the product it belongs to. */
      if (tab === 'ribbons') properties['_parent_handle'] = this.dataset.productHandle || '';

      /* Same pair as the parent. The fee line is the line an engraver picks, so
         it has to say what to engrave AND in which face; the font was the half
         that used to be missing here. */
      if (tab === 'engraving' && detail.text) {
        properties['Engraving Text'] = detail.text;
        if (detail.font) properties['Engraving Font'] = detail.font;
      }
      if (tab === 'postcard') {
        if (detail.message) properties['Post Card Message'] = detail.message;
        if (detail.image) {
          /* A file can only go through a multipart request, and multipart
             /cart/add.js takes exactly one item — so this line cannot ride in
             the JSON batch and is handed back for a second call. */
          postcardUpload = { variantId: detail.variantId, properties, file: detail.image };
          return;
        }
      }
      items.push({ id: parseInt(detail.variantId, 10), quantity: 1, properties });
    });

    return { items, postcardUpload };
  }

  get cartRenderTarget() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  async addToCart() {
    if (this.adding) return;
    this.adding = true;
    this.showSubmitError('');
    this.setSubmitBusy(true);

    const token = this.giftToken();
    const { items, postcardUpload } = this.buildGiftItems(token);
    const cart = this.cartRenderTarget;
    const sectionIds = cart ? cart.getSectionsToRender().map((section) => section.id) : [];
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    try {
      /* Sections are requested only on the final call so the HTML that comes
         back already contains every line in the group. */
      const batch = await this.postJson(`${root}cart/add.js`, {
        items,
        ...(postcardUpload ? {} : this.sectionsPayload(sectionIds)),
      });

      let response = batch;
      if (postcardUpload) {
        try {
          response = await this.postPostcard(root, postcardUpload, sectionIds);
        } catch (uploadError) {
          /* The charge-bearing lines are already in the cart. Saying nothing
             would hand over a gift with a paid-for postcard that does not
             exist, so this stops here and says exactly what is missing. */
          this.showSubmitError(
            'Your items were added but the post card photo could not be uploaded. Remove the post card from your cart and try again.'
          );
          this.refreshCart();
          return;
        }
      }

      this.dispatchEvent(
        new CustomEvent('gift:added', { bubbles: true, detail: { token, items, cartData: response } })
      );
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gift-personalise', cartData: response });
      }

      /* Closing is what clears the wizard — see bindModalLifecycle. */
      const modal = this.closest('modal-dialog');
      if (modal) modal.hide();
      this.renderCart(response);
    } catch (error) {
      this.showSubmitError(this.addErrorMessage(error));
    } finally {
      this.adding = false;
      this.setSubmitBusy(false);
    }
  }

  /* Emptied whenever the modal closes, whether the customer added the group or
     abandoned it. Closing means cancelling: the page's own Add to cart is
     wired to include whatever the wizard holds, so anything left behind would
     be bought silently — a second copy of every personalisation after an add,
     or add-ons the customer thought they had dismissed. */
  reset() {
    this.selections.clear();

    this.querySelectorAll('[data-gift-option-input], [data-gift-optin-input]').forEach((input) => {
      input.checked = false;
    });
    if (this.engravingInput) this.engravingInput.value = '';
    if (this.engravingPreview) this.engravingPreview.textContent = '';
    this.showEngravingError(false);
    if (this.postcardInput) this.postcardInput.value = '';
    if (this.postcardMessage) this.postcardMessage.textContent = '';
    this.useGenericCard();

    /* Also clears the hidden field on the product form — left enabled, it
       would ride along on an ordinary, unpersonalised add. */
    if (this.hideMrpInput) this.hideMrpInput.checked = false;
    this.syncHideMrp();

    this.showSubmitError('');
    this.renderTotals();
    this.activate(0);
  }

  sectionsPayload(sectionIds) {
    if (!sectionIds.length) return {};
    return { sections: sectionIds, sections_url: window.location.pathname };
  }

  async postJson(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    });
    const data = await response.json();
    /* /cart/add.js reports failures in the body with a `status`, not by
       returning a non-2xx, so an unchecked response reads as success. */
    if (data && data.status) throw data;
    return data;
  }

  async postPostcard(root, upload, sectionIds) {
    const body = new FormData();
    body.append('id', upload.variantId);
    body.append('quantity', '1');
    Object.entries(upload.properties).forEach(([key, value]) => {
      body.append(`properties[${key}]`, value);
    });
    body.append('properties[Post Card Image]', upload.file, 'post-card.jpg');
    if (sectionIds.length) {
      body.append('sections', sectionIds.join(','));
      body.append('sections_url', window.location.pathname);
    }

    /* No Content-Type header — the browser has to set the multipart boundary. */
    const response = await fetch(`${root}cart/add.js`, { method: 'POST', body });
    const data = await response.json();
    if (data && data.status) throw data;
    return data;
  }

  addErrorMessage(error) {
    if (error && error.description) return error.description;
    if (error && error.message) return error.message;
    return 'Something went wrong adding this to your cart. Please try again.';
  }

  renderCart(response) {
    const cart = this.cartRenderTarget;
    if (!cart) {
      window.location = window.routes.cart_url;
      return;
    }
    cart.classList.remove('is-empty');
    cart.setActiveElement(document.activeElement);
    cart.renderContents(response);
  }

  /* Used when the group landed only partially: the modal stays open with the
     error, but the cart badge must still reflect what did get added. */
  refreshCart() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((cartData) => {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gift-personalise', cartData });
        }
      })
      .catch(() => {});
  }

  setSubmitBusy(busy) {
    if (!this.nextButton) return;
    this.nextButton.disabled = busy;
    this.nextButton.setAttribute('aria-busy', String(busy));
    if (this.nextSpinner) this.nextSpinner.classList.toggle('hidden', !busy);
  }

  showSubmitError(message) {
    if (!this.submitError) return;
    this.submitError.textContent = message;
    this.submitError.hidden = !message;
  }

  /* The product form's own button must produce the same cart as the wizard's,
     or a customer who configures a gift and then uses the page's Add to cart
     silently loses every choice. */
  interceptFormSubmit() {
    document.addEventListener(
      'submit',
      (event) => {
        if (event.target !== this.productForm) return;
        if (!this.selections.size) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        /* Same refusal as the wizard's own button — but the customer cannot
           see the message from out here, so reopen on the offending tab. */
        if (!this.validateEngraving()) {
          this.activate(this.tabSlugs.indexOf('engraving'));
          const modal = this.closest('modal-dialog');
          if (modal) modal.show(event.target);
          if (this.engravingInput) this.engravingInput.focus();
          return;
        }
        this.addToCart();
      },
      true
    );
  }

  /* ---------- product form ---------- */

  get productForm() {
    return this.dataset.formId ? document.getElementById(this.dataset.formId) : null;
  }

  /* Mirrors the checkbox onto the hidden field in the product form. A
     disabled input is not submitted, which is how the property stays off the
     order entirely rather than riding along as "no". */
  syncHideMrp() {
    const form = this.productForm;
    if (!form || !this.hideMrpInput) return;
    const field = form.querySelector('[data-gift-hide-mrp-field]');
    if (!field) return;
    field.disabled = !this.hideMrpInput.checked;
    field.value = this.hideMrpInput.checked ? 'Yes' : '';
  }

  /* "Total with Product" has to name the variant the customer is actually
     buying, which can change on the product form while the modal is closed. */
  followVariantPrice() {
    if (this.unsubscribeVariant) return;
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const variant = event && event.data ? event.data.variant : null;
      if (!variant || event.data.sectionId !== this.dataset.sectionId) return;
      this.dataset.productPrice = variant.price;
      this.renderTotals();
    });
  }

  disconnectedCallback() {
    if (!this.unsubscribeVariant) return;
    this.unsubscribeVariant();
    this.unsubscribeVariant = null;
  }

  /* Opening: the shared modal adds `overflow-hidden` to <body>, which carries
     position:fixed and so resets the page scroll behind the modal and clips
     the PDP. Block scrolling on <html> instead and put the offset back.

     Closing: release that block and clear the wizard. Every exit — the X, Esc,
     a click on the backdrop, and the hide that follows a successful add — ends
     in `hide()`, so hooking the attribute it toggles is the one place all of
     them meet. */
  bindModalLifecycle() {
    const modal = this.closest('modal-dialog');
    if (!modal || modal.giftScrollPatched) return;
    modal.giftScrollPatched = true;

    const originalShow = modal.show;
    const self = this;
    modal.show = function (opener) {
      const scrollY = window.scrollY;
      originalShow.call(this, opener);
      document.body.classList.remove('overflow-hidden');
      window.scrollTo(0, scrollY);
      document.documentElement.style.overflow = 'hidden';
      /* Tracks could not be measured while the modal was display:none. */
      if (self.tracks) self.tracks.forEach((track) => track.sync());
    };

    /* hide() is bound in the base constructor, so it cannot be wrapped the
       same way — watch the attribute it toggles instead. */
    new MutationObserver(() => {
      if (modal.hasAttribute('open')) return;
      document.documentElement.style.overflow = '';
      self.reset();
    }).observe(modal, { attributes: true, attributeFilter: ['open'] });
  }
}

customElements.define('gift-personalise', GiftPersonalise);
